import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFilterPersistenceContextOptional } from './filter-persistence-context';
import * as organisationSelector from '@modules/organisation/selectors/organisation';
import type { StoreState } from '@common/types/store';

/**
 * Clears filter persistence context when organisation or network changes.
 * - On organisation change: clear all (full context + sessionStorage is updated by provider).
 * - On network change: clear only keys for the previous network.
 */
export function FilterPersistenceClearOnChange() {
  const context = useFilterPersistenceContextOptional();
  const organisation = useSelector((state: StoreState) => organisationSelector.selected(state));
  const selectedNetworkId = useSelector((state: StoreState) => state.network?.selected);

  const prevOrgIdRef = useRef<string | null>(null);
  const prevNetworkIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!context) return;

    const orgId = organisation?.id ?? null;
    if (prevOrgIdRef.current !== null && prevOrgIdRef.current !== orgId) {
      context.clearAllFilters();
    }
    prevOrgIdRef.current = orgId;
  }, [context, organisation?.id]);

  useEffect(() => {
    if (!context) return;

    const networkId = selectedNetworkId ?? null;
    if (prevNetworkIdRef.current !== null && prevNetworkIdRef.current !== networkId) {
      context.clearFiltersForNetwork(prevNetworkIdRef.current);
    }
    prevNetworkIdRef.current = networkId;
  }, [context, selectedNetworkId]);

  return null;
}
